import React, { useState } from 'react'
import {
    Container,
    IconButton,
    TableCell,
    TableRow,
    Tooltip,
    useTheme,
    CircularProgress,
} from '@material-ui/core'
import AddIcon from '@material-ui/icons/Add'
import EditIcon from '@material-ui/icons/Edit'
import { SimpleTable, HeadCell, RowData, CellData } from '../../../components'
import { Provider, ProviderBillingMode } from '../../../models/Outreach/types'
import { ProviderDialog } from './ProviderDialog'
import { typographyStyle } from '../../../theme/typographyStyle'

interface ProvidersTabProps {
    providers: Provider[]
    isLoading?: boolean
    onUpdateProvider: (
        id: number,
        provider: {
            name: string
            credit_cost: number
            billing_mode: ProviderBillingMode
        },
    ) => Promise<void>
    onCreateProvider: (provider: {
        name: string
        credit_cost: number
        billing_mode: ProviderBillingMode
    }) => Promise<void>
    onDeleteProvider?: (id: number) => Promise<void>
}

interface ProviderRowType extends RowData {
    name: CellData<string>
    creditCost: CellData<string>
    billingMode: CellData<string>
    provider: CellData<Provider>
}

export const ProvidersTab: React.FC<ProvidersTabProps> = ({
    providers,
    isLoading = false,
    onUpdateProvider,
    onCreateProvider,
    onDeleteProvider,
}) => {
    const theme = useTheme()
    const [dialogOpen, setDialogOpen] = useState(false)
    const [editingProvider, setEditingProvider] = useState<Provider | null>(
        null,
    )

    const headCellStyle: React.CSSProperties = {
        ...typographyStyle(theme.typography.h6),
        fontWeight: 500,
    }

    const headCells: HeadCell<ProviderRowType>[] = [
        {
            disablePadding: false,
            align: 'left',
            id: 'name',
            label: 'Name',
            style: headCellStyle,
        },
        {
            disablePadding: false,
            align: 'center',
            id: 'creditCost',
            label: 'Credit Cost',
            style: headCellStyle,
        },
        {
            disablePadding: false,
            align: 'center',
            id: 'billingMode',
            label: 'Billing Mode',
            style: headCellStyle,
        },
        {
            disablePadding: false,
            align: 'center',
            id: 'actions',
            label: 'Actions',
            style: headCellStyle,
        },
    ]

    const createProviderRow = (provider: Provider): ProviderRowType => ({
        name: {
            value: provider.name,
            sortValue: provider.name,
        },
        creditCost: {
            value: provider.credit_cost?.toString() || '0',
            sortValue: provider.credit_cost || 0,
        },
        billingMode: {
            value: provider.billing_mode || '',
            sortValue: provider.billing_mode || '',
        },
        provider: {
            value: provider,
            sortValue: provider.name,
        },
    })

    const handleAddClick = () => {
        setEditingProvider(null)
        setDialogOpen(true)
    }

    const handleEditClick = (provider: Provider) => {
        setEditingProvider(provider)
        setDialogOpen(true)
    }

    const handleDialogClose = () => {
        setDialogOpen(false)
        setEditingProvider(null)
    }

    const handleDialogSave = async (providerData: {
        name: string
        credit_cost: number
        billing_mode: ProviderBillingMode
    }) => {
        try {
            if (editingProvider) {
                await onUpdateProvider(editingProvider.id, providerData)
            } else {
                await onCreateProvider(providerData)
            }
            handleDialogClose()
        } catch (error) {
            // Error handling is done in the mutation's onError callback
            console.error('Error saving provider:', error)
        }
    }

    if (isLoading) {
        return (
            <Container
                style={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: theme.palette.grey[200],
                }}
            >
                <CircularProgress />
            </Container>
        )
    }

    return (
        <Container
            style={{
                flex: 1,
                flexDirection: 'column',
                backgroundColor: theme.palette.grey[200],
                height: '100%',
                overflow: 'hidden',
            }}
        >
            <Container
                style={{
                    padding: theme.spacing(2),
                    justifyContent: 'flex-end',
                    flexShrink: 0,
                }}
            >
                <Tooltip title="Add Provider">
                    <IconButton onClick={handleAddClick} color="primary">
                        <AddIcon />
                    </IconButton>
                </Tooltip>
            </Container>

            <Container
                style={{
                    flex: 1,
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                    padding: `0 ${theme.spacing(2)}px ${theme.spacing(2)}px`,
                }}
            >
                <SimpleTable
                    rows={providers.map((provider) =>
                        createProviderRow(provider),
                    )}
                    orderByDefault="name"
                    headCells={headCells}
                    maxHeight="calc(100vh - 220px)"
                    render={(row) => (
                        <TableRow key={row.provider.value.id}>
                            <TableCell>{row.name.value}</TableCell>
                            <TableCell align="center">
                                {row.creditCost.value}
                            </TableCell>
                            <TableCell align="center">
                                {row.billingMode.value}
                            </TableCell>
                            <TableCell align="center">
                                <Tooltip title="Edit">
                                    <IconButton
                                        onClick={() =>
                                            handleEditClick(row.provider.value)
                                        }
                                    >
                                        <EditIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </TableCell>
                        </TableRow>
                    )}
                />
            </Container>

            <ProviderDialog
                open={dialogOpen}
                onClose={handleDialogClose}
                onSave={handleDialogSave}
                provider={editingProvider}
            />
        </Container>
    )
}
